import React, { useState, useEffect, useCallback } from 'react';
import LeadForm from '../LeadForm';
import './LeadModal.css';

/**
 * LeadModal
 *
 * A single site-wide enquiry popup. It has NO visible trigger of its own —
 * it opens when any button dispatches the `open-lead-modal` window event.
 * Use the exported openLeadModal() helper from any component/button.
 *
 * Props:
 *   defaultProjectId {number}  – forwarded to LeadForm (1–4)
 */

// Call from anywhere (e.g. a nav CTA button's onClick) to open the popup.
export function openLeadModal() {
  window.dispatchEvent(new CustomEvent('open-lead-modal'));
}

export default function LeadModal({ defaultProjectId = 4 }) {
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => setIsOpen(false), []);

  /* ── Open on the global event (fired by any CTA button) ── */
  useEffect(() => {
    const onOpen = () => setIsOpen(true);
    window.addEventListener('open-lead-modal', onOpen);
    return () => window.removeEventListener('open-lead-modal', onOpen);
  }, []);

  /* ── Escape key ─────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  /* ── Pause / resume Lenis + body scroll ─────────── */
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('lenis:stop'));
      document.body.style.overflow = 'hidden';
    } else {
      window.dispatchEvent(new CustomEvent('lenis:start'));
      document.body.style.overflow = '';
    }
    return () => {
      window.dispatchEvent(new CustomEvent('lenis:start'));
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* No floating trigger — the popup opens via the openLeadModal() event
          from the header "Reserve a Plot" CTA and other CTA buttons. */}

      {/* ── Overlay + Panel ──────────────────────── */}
      {isOpen && (
        <div
          className="lm-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
          role="presentation"
        >
          <div
            className="lm-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Enquiry form"
          >
            {/* Header */}
            <div className="lm-header">
              <div className="lm-header__text">
                <p className="lm-header__eyebrow">Nausiddhi Realty</p>
                <h2 className="lm-header__title">Schedule a Callback</h2>
              </div>
              <button className="lm-close" onClick={close} aria-label="Close enquiry form">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <line x1="1" y1="1" x2="15" y2="15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                  <line x1="15" y1="1" x2="1"  y2="15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* Gold rule */}
            <div className="lm-rule" aria-hidden="true" />

            {/* Form */}
            <div className="lm-body">
              <LeadForm
                defaultProjectId={defaultProjectId}
                onSuccess={close}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
